import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import stringHelpers from "../../../utilities/stringHelpers"
import { toDateString } from "../../../utilities/dateUtilities"
import Enums from "../../../utilities/enum"
import { TagSelector } from "../../shared/tagSelector"
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../../utilities/arrayUtilities"

export const ClientBreachesFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialFilterStart,
    initialFilterEnd,
    initialSiteId,
    initialType,
    initialStatus,
    initialSelectedTags = [],
    initialAppliedFilters,
    sites = [],
    tags = []
}) => {
    const [filterStart, setFilterStart] = useState(initialFilterStart)
    const [filterEnd, setFilterEnd] = useState(initialFilterEnd)
    const [siteId, setSiteId] = useState(initialSiteId)
    const [type, setType] = useState(initialType)
    const [status, setStatus] = useState(initialStatus)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const clearLocalFilters = () => {
        setFilterStart(null)
        setFilterEnd(null)
        setSiteId(null)
        setType(null)
        setStatus(null)
        setSelectedTags([])
        setAppliedFilters(null)
    }

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    return (
        <SearchFiltersModal
            title="Filter Results"
            handleClose={ handleClose }
            handleConfirm={ () => handleConfirm({ filterStart, filterEnd, siteId, type, status, selectedTags, appliedFilters }) }
            showModal={ showModal }
            key="client-breaches-filters-modal"
            handleReset={ clearLocalFilters }
        >
            <div className="w-100">
                <div className="mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
                    <DateRangePicker
                        fromDate={ filterStart }
                        toDate={ filterEnd }
                        setDateRange={ (firstDate, lastDate, presetLabel) => {
                            const isFirstDateEmpty = firstDate === null || firstDate === ""
                            const isLastDateEmpty = lastDate === null || lastDate === ""
                            setFilterStart(isFirstDateEmpty ? null : firstDate)
                            setFilterEnd(isLastDateEmpty ? null : lastDate)
                            if (!stringHelpers.isNullOrEmpty(presetLabel)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters.From
                                    delete updatedFilters.To
                                    updatedFilters["Date Range"] = presetLabel
                                    return updatedFilters
                                })
                            }
                            else {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    if (!isFirstDateEmpty && !isLastDateEmpty) {
                                        delete updatedFilters.From
                                        delete updatedFilters.To
                                        updatedFilters["Date Range"] = `${ toDateString(new Date(firstDate)) } - ${ toDateString(new Date(lastDate)) }`
                                    } else {
                                        if (!isFirstDateEmpty) {
                                            updatedFilters.From = toDateString(new Date(firstDate))
                                        } else {
                                            delete updatedFilters.From
                                        }
                                        if (!isLastDateEmpty) {
                                            updatedFilters.To = toDateString(new Date(lastDate))
                                        } else {
                                            delete updatedFilters.To
                                        }
                                        delete updatedFilters["Date Range"]
                                    }
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            }
                        } }
                        showClearOption
                        hideLabel
                        fullWidth
                        dropdownPositionRight
                    />
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...sites.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setSiteId(stringHelpers.isNullOrEmpty(value) ? null : value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                const selectedSite = sites.find(item => item.id === value)
                                setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite?.name }))
                            }
                        } }
                        selected={ siteId ?? "" }
                    />
                </div>

                { !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                        <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                    </div>
                    <TagSelector
                        onChange={ handleTagsChange }
                        selectedTags={ selectedTags }
                        tags={ tags }
                    />
                </div> }

                <div className="d-flex flex-column mb-2">
                    <label htmlFor="client-breaches-type-selector" className="m-0 mr-2 mb-1 nowrap">Type</label>
                    <select
                        id="client-breaches-type-selector"
                        className="border rounded px-2 py-1 w-100"
                        value={ type === null ? "" : String(type) }
                        onChange={ e => {
                            const value = e.target.value === "" ? null : Number(e.target.value)
                            setType(value)
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                if (value === null) {
                                    delete updatedFilters["Type"]
                                } else {
                                    updatedFilters["Type"] = Enums.ContraventionTypes[value]
                                }
                                return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                            })
                        } }
                    >
                        <option value="">All</option>
                        <option value="0">ANPR</option>
                        <option value="1">MNPR</option>
                    </select>
                </div>

                <div className="d-flex flex-column mb-2">
                    <label htmlFor="client-breaches-status-selector" className="m-0 mr-2 mb-1 nowrap">Status</label>
                    <select
                        id="client-breaches-status-selector"
                        className="border rounded px-2 py-1 w-100"
                        value={ status === null ? "" : String(status) }
                        onChange={ e => {
                            const value = e.target.value === "" ? null : Number(e.target.value)
                            setStatus(value)
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                if (value === null) {
                                    delete updatedFilters["Status"]
                                } else {
                                    updatedFilters["Status"] = Enums.BreachStatus[value]
                                }
                                return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                            })
                        } }
                    >
                        <option value="">All</option>
                        <option value="0">New</option>
                        <option value="503">Void</option>
                    </select>
                </div>
            </div>
        </SearchFiltersModal>
    )
}
